import Image from 'components/Common/Image'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { isExternalUrl, removeRegionFromPath } from 'lib/url/searchUrlUtils'
import type { LoyaltyOffersHubHeroCarouselItem } from 'loyalty/lib/loyaltyOffersHub/types'
import TextButton from 'LuxKit/Button/TextButton'
import Label from 'LuxKit/Label'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import styled from 'styled-components'

const HERO_LOGO_IMAGE_MAX_WIDTH = 160
const HERO_LOGO_IMAGE_MAX_HEIGHT = 70

const HeroCardContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;
  width: 100%;
  padding: ${rem(24)} ${rem(24)} ${rem(12)};
  overflow: hidden;
  gap: ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  ${mediaQueryUp.tablet} {
    max-width: ${rem(350)};
    padding: ${rem(32)};
    border-radius: ${props => props.theme.borderRadius.L};
  }
`

const HeroLogoImageContainer = styled.div`
  display: inline-flex;
  align-items: flex-start;
  max-width: ${rem(HERO_LOGO_IMAGE_MAX_WIDTH)};
  max-height: ${rem(HERO_LOGO_IMAGE_MAX_HEIGHT)};
`

const HeroLogoImage = styled(Image)`
  display: block;
  max-width: ${rem(HERO_LOGO_IMAGE_MAX_WIDTH)};
  max-height: ${rem(HERO_LOGO_IMAGE_MAX_HEIGHT)};
`

const HeroCtaButton = styled(TextButton)`
  align-self: flex-end;
`

interface Props {
  item: LoyaltyOffersHubHeroCarouselItem
}

function LoyaltyOffersHubHeroOfferContent({ item }: Props) {
  const isCtaExternalLink = isExternalUrl(item.ctaUrl)

  return <HeroCardContent>
    <Group direction="vertical" gap={16}>
      <Label kind="primary" variant="highlight">{item.label}</Label>
      {!!item.logoImageId && <HeroLogoImageContainer>
        <HeroLogoImage
          id={item.logoImageId}
        />
      </HeroLogoImageContainer>}
      <Group direction="vertical" gap={8}>
        <Heading variant="heading4" as="h2" lineClamp={3}>
          {item.title}
        </Heading>
        <BodyText variant="medium" colour="neutral-one" lineClamp={5} tabletLineClamp={4} desktopLineClamp={4}>
          {item.description}
        </BodyText>
      </Group>
    </Group>
    <HeroCtaButton
      kind="primary"
      size="large"
      to={isCtaExternalLink ? undefined : removeRegionFromPath(item.ctaUrl)}
      href={isCtaExternalLink ? item.ctaUrl : undefined}
      target={isCtaExternalLink ? '_blank' : undefined}
      rel={isCtaExternalLink ? 'noopener noreferrer' : undefined}
    >
      {item.ctaLabel}
    </HeroCtaButton>
  </HeroCardContent>
}

export default LoyaltyOffersHubHeroOfferContent
